/**
 * 一人称入力。
 *
 * - マウス移動（ポインタロック中）または画面ドラッグ（タッチプレイ） → 視点
 * - WASD / Space / Shift、またはスティック / ジャンプ → 移動意思（MoveIntent）
 * - クリック・ホイール・数字キー / X / Z → 離散アクション（設置・破壊・持ち替えなど）
 *
 * ポインタロック中はブラウザのカーソルが消えるので、UI 操作は Esc メニュー、
 * E インベントリ（hud.ts）、V グラフィック設定、G グリッド編集で受け持つ。
 * タッチ端末ではインベントリを出さず、スティックとジャンプとドラッグ視点だけ。
 */

import type { MoveIntent } from './player';
import { prefersTouchPlay } from './touch-play';

export type InputAction =
  | 'use'
  | 'pick'
  | 'toggle-fly'
  | 'toggle-debug'
  | 'commit'
  | 'undo-point'
  | 'cancel'
  | 'hotbar'
  | 'hotbar-delta'
  | 'hotbar-destroy'
  | 'hotbar-previous'
  | 'thickness'
  | 'inventory'
  | 'graphics'
  | 'grid-editor'
  | 'playerlist';

export interface InputHandlers {
  onLockChange(locked: boolean): void;
  onAction(action: InputAction, value?: number): void;
}

/** マウス感度の基準値（1.0 のときの rad/px） */
const BASE_SENSITIVITY = 0.0022;
/** タッチの視点ドラッグは指の移動が大きいので、同じスライダー値でも速く回す */
const TOUCH_LOOK_SCALE = 3;
/** ダブルタップと判定する間隔（ms） */
const DOUBLE_TAP_MS = 300;
/** 上下の視点制限 */
const PITCH_LIMIT = Math.PI / 2 - 0.001;

export class InputController {
  /** マウス感度（0.2 〜 3.0 程度を想定） */
  sensitivity = 1;
  /** ホットバーのホイール方向を逆にする */
  invertHotbarScroll = false;
  /** 押しっぱなしのクリック（連続設置・連続破壊に使う） */
  useHeld = false;
  locked = false;
  /** ポインタロックなしのタッチプレイ。スティックとドラッグ視点だけ有効 */
  touchPlaying = false;

  private readonly pressed = new Set<string>();
  /** 左右クリックの同時押しでも長押しが途切れないようにボタン単位で覚える */
  private readonly useButtons = new Set<number>();
  private lookX = 0;
  private lookY = 0;
  private virtualForward = 0;
  private virtualStrafe = 0;
  private virtualJump = false;
  private lastSpaceTap = 0;
  private lastForwardTap = 0;
  private sprintLatched = false;
  /**
   * 短く押しただけの Space を取りこぼさないためのラッチ。
   * キーの押下はフレーム間で起きうるので、押された事実を次の intent() まで覚えておく。
   */
  private jumpLatched = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly handlers: InputHandlers
  ) {
    this.attach();
  }

  /* ---------------------------------------------------------------- */
  /* ポインタロック                                                    */
  /* ---------------------------------------------------------------- */

  requestLock(): void {
    if (prefersTouchPlay()) {
      this.setTouchPlaying(true);
      this.handlers.onLockChange(true);
      return;
    }
    if (this.locked) return;
    // Esc 直後の再ロックはブラウザに拒否されることがあるので握りつぶす
    const result = this.canvas.requestPointerLock() as unknown as Promise<void> | undefined;
    if (result && typeof result.catch === 'function') result.catch(() => undefined);
  }

  releaseLock(): void {
    if (this.touchPlaying) {
      this.setTouchPlaying(false);
      this.handlers.onLockChange(false);
      return;
    }
    if (document.pointerLockElement === this.canvas) document.exitPointerLock();
  }

  setTouchPlaying(active: boolean): void {
    this.touchPlaying = active;
    document.body.classList.toggle('touch-playing', active);
    if (active) return;
    this.virtualForward = 0;
    this.virtualStrafe = 0;
    this.virtualJump = false;
    this.jumpLatched = false;
  }

  setVirtualMove(forward: number, strafe: number): void {
    this.virtualForward = forward;
    this.virtualStrafe = strafe;
  }

  setVirtualJump(held: boolean): void {
    if (held && !this.virtualJump) {
      this.jumpLatched = true;
      const now = performance.now();
      if (now - this.lastSpaceTap < DOUBLE_TAP_MS) {
        this.handlers.onAction('toggle-fly');
        this.lastSpaceTap = 0;
      } else {
        this.lastSpaceTap = now;
      }
    }
    this.virtualJump = held;
  }

  addLookPixels(dx: number, dy: number): void {
    this.lookX += dx;
    this.lookY += dy;
  }

  private attach(): void {
    document.addEventListener('pointerlockchange', () => {
      this.locked = document.pointerLockElement === this.canvas;
      if (!this.locked) {
        this.pressed.clear();
        this.useButtons.clear();
        this.useHeld = false;
        this.sprintLatched = false;
        this.jumpLatched = false;
      }
      this.handlers.onLockChange(this.locked);
    });

    document.addEventListener('mousemove', (event) => {
      if (!this.locked) return;
      this.lookX += event.movementX;
      this.lookY += event.movementY;
    });

    this.canvas.addEventListener('mousedown', (event) => {
      if (!this.locked) return;
      event.preventDefault();
      if (event.button === 0 || event.button === 2) {
        this.useButtons.add(event.button);
        this.useHeld = true;
        this.handlers.onAction('use');
      } else if (event.button === 1) {
        this.handlers.onAction('pick');
      }
    });

    window.addEventListener('mouseup', (event) => {
      if (event.button === 0 || event.button === 2) {
        this.useButtons.delete(event.button);
        this.useHeld = this.useButtons.size > 0;
      }
    });

    this.canvas.addEventListener('contextmenu', (event) => event.preventDefault());

    this.canvas.addEventListener(
      'wheel',
      (event) => {
        if (!this.locked) return;
        event.preventDefault();
        const direction = Math.sign(event.deltaY);
        if (direction === 0) return;
        // Ctrl・Alt: 太さ調整 / 通常: ホットバー
        if (event.ctrlKey || event.altKey) this.handlers.onAction('thickness', -direction);
        else {
          this.handlers.onAction(
            'hotbar-delta',
            this.invertHotbarScroll ? -direction : direction
          );
        }
      },
      { passive: false }
    );

    window.addEventListener('keydown', (event) => this.onKeyDown(event));
    window.addEventListener('keyup', (event) => this.onKeyUp(event));
    window.addEventListener('blur', () => {
      this.pressed.clear();
      this.useButtons.clear();
      this.useHeld = false;
    });
  }

  /* ---------------------------------------------------------------- */
  /* キーボード                                                        */
  /* ---------------------------------------------------------------- */

  private onKeyDown(event: KeyboardEvent): void {
    const target = event.target as HTMLElement | null;
    if (target && (target.tagName === 'INPUT' || target.tagName === 'SELECT')) return;

    if (!this.locked && !this.touchPlaying) return;

    if (event.code === 'Tab') {
      event.preventDefault();
      this.handlers.onAction('playerlist', 1);
      return;
    }
    if (event.repeat) {
      this.pressed.add(event.code);
      return;
    }

    this.pressed.add(event.code);

    switch (event.code) {
      case 'Space': {
        this.jumpLatched = true;
        const now = performance.now();
        if (now - this.lastSpaceTap < DOUBLE_TAP_MS) {
          this.handlers.onAction('toggle-fly');
          this.lastSpaceTap = 0;
        } else {
          this.lastSpaceTap = now;
        }
        event.preventDefault();
        return;
      }
      case 'KeyW': {
        const now = performance.now();
        if (now - this.lastForwardTap < DOUBLE_TAP_MS) this.sprintLatched = true;
        this.lastForwardTap = now;
        return;
      }
      case 'KeyF':
        this.handlers.onAction('toggle-fly');
        return;
      case 'KeyG':
        event.preventDefault();
        this.handlers.onAction('grid-editor');
        return;
      case 'KeyE':
        event.preventDefault();
        this.handlers.onAction('inventory');
        return;
      case 'KeyV':
        event.preventDefault();
        this.handlers.onAction('graphics');
        return;
      case 'Enter':
        this.handlers.onAction('commit');
        return;
      case 'Backspace':
      case 'Delete':
        event.preventDefault();
        this.handlers.onAction('undo-point');
        return;
      case 'KeyQ':
        this.handlers.onAction('cancel');
        return;
      case 'KeyX':
        this.handlers.onAction('hotbar-destroy');
        return;
      case 'KeyZ':
        this.handlers.onAction('hotbar-previous');
        return;
      case 'F3':
        event.preventDefault();
        this.handlers.onAction('toggle-debug');
        return;
      case 'BracketLeft':
        this.handlers.onAction('thickness', -1);
        return;
      case 'BracketRight':
        this.handlers.onAction('thickness', 1);
        return;
      default:
    }

    const slot = /^Digit([0-9])$/.exec(event.code);
    if (slot) this.handlers.onAction('hotbar', Number(slot[1]));
  }

  private onKeyUp(event: KeyboardEvent): void {
    this.pressed.delete(event.code);
    if (event.code === 'KeyW') this.sprintLatched = false;
    if (event.code === 'Tab') this.handlers.onAction('playerlist', 0);
  }

  /* ---------------------------------------------------------------- */
  /* フレームごとの読み出し                                            */
  /* ---------------------------------------------------------------- */

  /** 溜まったマウス移動量を rad に変換して取り出す（取り出すと 0 に戻る） */
  consumeLook(): { yaw: number; pitch: number } {
    const factor = BASE_SENSITIVITY * this.sensitivity * (this.touchPlaying ? TOUCH_LOOK_SCALE : 1);
    const yaw = -this.lookX * factor;
    const pitch = -this.lookY * factor;
    this.lookX = 0;
    this.lookY = 0;
    return { yaw, pitch };
  }

  intent(): MoveIntent {
    if (!this.locked && !this.touchPlaying) {
      this.jumpLatched = false;
      return { forward: 0, strafe: 0, vertical: 0, jump: false, sprint: false, sneak: false };
    }
    const forward = clampAxis(
      (this.pressed.has('KeyW') ? 1 : 0) - (this.pressed.has('KeyS') ? 1 : 0) + this.virtualForward
    );
    const strafe = clampAxis(
      (this.pressed.has('KeyD') ? 1 : 0) - (this.pressed.has('KeyA') ? 1 : 0) + this.virtualStrafe
    );
    const sneak = this.pressed.has('ShiftLeft') || this.pressed.has('ShiftRight');
    const jump = this.pressed.has('Space') || this.virtualJump || this.jumpLatched;
    this.jumpLatched = false;
    const sprint =
      this.sprintLatched || this.pressed.has('ControlLeft') || this.pressed.has('ControlRight');
    return {
      forward,
      strafe,
      vertical: (jump ? 1 : 0) - (sneak ? 1 : 0),
      jump,
      sprint,
      sneak,
    };
  }
}

/** pitch を可動域に収める */
export function clampPitch(pitch: number): number {
  return Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch));
}

function clampAxis(value: number): number {
  return Math.max(-1, Math.min(1, value));
}
